import { Chart, ChartTypes, ChartInfoIcon, formatNumber } from "@uicore";
import { Skeleton, Stack, Typography } from "@altimateai/lego";
import { useClusterCost } from "../hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { Bar } from "recharts";
import { formatChartDate } from "../helpers";

interface Props {
  clusterId: string;
  startDate: string;
  endDate: string;
  aggregation?: "day" | "week" | "month";
}

const ClusterCostChart = ({ clusterId, startDate, endDate, aggregation = "day" }: Props) => {
  const { data, isLoading, error } = useClusterCost(
    { cluster_id: clusterId, start_date: startDate, end_date: endDate, granularity: aggregation },
    Boolean(clusterId)
  );
  const { currency, currencySymbol } = useAppState();

  const chartData =
    data?.timeseries?.map(point => ({
      period: point.period,
      cost: point.estimated_cost,
      dbus: point.total_dbus,
    })) ?? [];

  if (isLoading) {
    return (
      <Stack direction="column" gap="md">
        <Skeleton className="al-h-6 al-w-48" />
        <Skeleton className="al-h-[300px] al-w-full" />
      </Stack>
    );
  }

  if (error || !data) {
    return <Typography className="al-text-red-500">Failed to load cost data</Typography>;
  }

  if (chartData.length === 0) {
    return (
      <Stack align="center" justify="center" className="al-h-[300px]">
        <Typography className="al-text-gray-500">
          No cost data available for the selected period
        </Typography>
      </Stack>
    );
  }

  const totalCost = data.estimated_cost;
  const totalDBUs = data.total_dbus;
  const dailyAvgCost = data.daily_average_cost;

  return (
    <div className="al-pb-8" style={{ overflow: "visible" }}>
      <Stack direction="row" align="center" gap="md" className="al-mb-4">
        <Stack direction="row" align="center" gap="xs">
          <div className="al-h-3 al-w-3 al-rounded" style={{ backgroundColor: "#5B9FFF" }} />
          <Typography size="sm" className="al-text-gray-600">
            Total: {formatNumber(totalCost, { currency, maximumFractionDigits: 2 })}
          </Typography>
          <ChartInfoIcon
            title="How to read this chart"
            content="Each bar represents the estimated cost for this cluster on that day. The height shows the daily cost. Total and average costs are shown in the legend above."
          />
        </Stack>
        <Typography size="sm" weight="medium" className="al-text-gray-700">
          Avg: {formatNumber(dailyAvgCost, { currency, maximumFractionDigits: 2 })}/day
        </Typography>
        <Typography size="sm" className="al-text-gray-500">
          {formatNumber(totalDBUs, { maximumFractionDigits: 0 })} DBUs
        </Typography>
      </Stack>
      <div style={{ width: "100%", height: 520, overflow: "visible" }}>
        <Chart
          title=""
          xAxisDataKey="period"
          yAxisLabel={{ value: "Cost" }}
          xAxisLabelFormatter={(value: string) => formatChartDate(value, aggregation)}
          yAxisLabelFormatter={(value: string) => `${currencySymbol}${Number(value).toFixed(0)}`}
          type={ChartTypes.BarChart}
          data={chartData}
          height={520}
        >
          {({ barSize }: { barSize: number }) => (
            <Bar
              barSize={barSize}
              name="Cost"
              dataKey="cost"
              fill="#5B9FFF"
              radius={[4, 4, 0, 0]}
            />
          )}
        </Chart>
      </div>
    </div>
  );
};

export default ClusterCostChart;
